import React, {Fragment, useState} from 'react';
import {Menu, Transition} from '@headlessui/react';
import {
    BuildingStorefrontIcon,
    CheckIcon,
    ChevronUpDownIcon,
    ArrowPathIcon
} from '@heroicons/react/24/outline';
import {useNavigate} from 'react-router-dom';
import {useQueryClient} from 'react-query';
import {toast} from 'react-toastify';
import {useTranslation} from 'react-i18next';
import {clsx} from 'clsx';
import useAuthStore from '../store/auth';
import {apollo} from '../utils/apollo';
import {SWITCH_MERCHANT} from '../utils/graphql';

function MerchantSwitcher({className}) {
    const {t} = useTranslation();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const {merchant, merchants, setUserAuth} = useAuthStore();
    const [switching, setSwitching] = useState(false);

    // List of merchants available to this user
    const merchantList = merchants && merchants.length > 0
        ? merchants
        : merchant
            ? [{
                merchantUuid: merchant.merchantUuid,
                merchantName: merchant.name,
                currency: merchant.currency,
                active: merchant.active,
                userRole: 'owner',
                isCurrent: true,
            }]
            : [];

    const currentMerchantName = merchant?.name || merchantList.find(m => m.isCurrent)?.merchantName || t('_.select_merchant') || 'Merchant';

    const handleSwitch = async (targetMerchantUuid) => {
        if (!targetMerchantUuid || targetMerchantUuid === merchant?.merchantUuid || switching) {
            return;
        }

        try {
            setSwitching(true);
            const {data} = await apollo.mutate({
                mutation: SWITCH_MERCHANT,
                variables: {
                    input: {
                        merchantUuid: targetMerchantUuid,
                    },
                },
            });

            const result = data?.switchMerchant;
            if (result?.success) {
                setUserAuth({
                    merchant: result.merchant,
                    merchants: result.merchants || [],
                    roles: result.roles || [],
                    permissions: result.permissions || [],
                });

                // Clear and invalidate caches for clean state
                await apollo.clearStore().catch(() => {});
                queryClient.clear();
                queryClient.invalidateQueries();

                window.dispatchEvent(new CustomEvent('resetReactQueryClient', {
                    detail: {cacheKey: null}
                }));

                toast.success(result.message || t('_.merchant_switched_successfully') || 'Switched merchant successfully');
                navigate('/dashboard');
            } else {
                toast.error(result?.message || t('_.failed_to_switch_merchant') || 'Failed to switch merchant');
            }
        } catch (error) {
            console.error('Error switching merchant:', error);
            const errorMsg = error?.graphQLErrors?.[0]?.message || error?.message || t('_.error_switching_merchant') || 'Error switching merchant';
            toast.error(errorMsg);
        } finally {
            setSwitching(false);
        }
    };

    return (
        <Menu as="div" className={clsx("relative inline-block text-left", className)}>
            <div>
                <Menu.Button
                    disabled={switching}
                    className="group flex items-center gap-x-2 px-3 py-1.5 rounded-full hover:bg-cyan-50/50 transition-all duration-200 border border-transparent hover:border-cyan-100 disabled:opacity-60"
                >
                    <div className="p-1 rounded-full bg-cyan-50 text-[#0b6a8c] group-hover:bg-[#0b6a8c] group-hover:text-white transition-colors duration-200">
                        {switching ? (
                            <ArrowPathIcon className="h-4 w-4 animate-spin" aria-hidden="true"/>
                        ) : (
                            <BuildingStorefrontIcon className="h-4 w-4" aria-hidden="true"/>
                        )}
                    </div>
                    <div className="flex flex-col text-left ltr:text-left rtl:text-right max-w-[140px] sm:max-w-[180px]">
                        <span className="text-xs font-bold text-gray-800 group-hover:text-[#0b6a8c] truncate leading-tight transition-colors">
                            {currentMerchantName}
                        </span>
                        {merchant?.currency && (
                            <span className="text-[10px] text-gray-400 font-medium leading-none">
                                {merchant.currency}
                            </span>
                        )}
                    </div>
                    {merchantList.length > 1 && (
                        <ChevronUpDownIcon className="h-4 w-4 text-gray-400 group-hover:text-[#0b6a8c] transition-colors" aria-hidden="true"/>
                    )}
                </Menu.Button>
            </div>

            {merchantList.length > 1 && (
                <Transition
                    as={Fragment}
                    enter="transition ease-out duration-100"
                    enterFrom="transform opacity-0 scale-95"
                    enterTo="transform opacity-100 scale-100"
                    leave="transition ease-in duration-75"
                    leaveFrom="transform opacity-100 scale-100"
                    leaveTo="transform opacity-0 scale-95"
                >
                    <Menu.Items className="absolute right-0 rtl:left-0 rtl:right-auto z-50 mt-2.5 w-72 origin-top-right rtl:origin-top-left rounded-2xl bg-white p-2 shadow-2xl ring-1 ring-gray-900/5 focus:outline-none border border-gray-100 divide-y divide-gray-50">
                        <div className="px-3 py-2 text-xs font-bold text-gray-400 uppercase tracking-wider">
                            {t('_.switch_merchant') || 'Your Merchants'}
                        </div>
                        <div className="py-1 space-y-1">
                            {merchantList.map((m) => {
                                const isCurrent = m.isCurrent || m.merchantUuid === merchant?.merchantUuid;
                                return (
                                    <Menu.Item key={m.merchantUuid}>
                                        {({active}) => (
                                            <button
                                                type="button"
                                                onClick={() => handleSwitch(m.merchantUuid)}
                                                disabled={isCurrent || switching}
                                                className={clsx(
                                                    'w-full flex items-center justify-between px-3 py-2.5 rounded-xl text-xs font-bold transition-all text-left rtl:text-right',
                                                    isCurrent
                                                        ? 'bg-cyan-50/80 text-[#0b6a8c] cursor-default'
                                                        : active
                                                            ? 'bg-gray-50 text-gray-900'
                                                            : 'text-gray-700 hover:bg-gray-50',
                                                    switching ? 'opacity-60 cursor-not-allowed' : ''
                                                )}
                                            >
                                                <div className="flex items-center gap-x-2.5 truncate">
                                                    <div className={clsx(
                                                        "h-2 w-2 rounded-full shrink-0",
                                                        m.active ? "bg-emerald-500 shadow-[0_0_6px_rgba(16,185,129,0.5)]" : "bg-amber-400"
                                                    )}/>
                                                    <div className="truncate">
                                                        <div className="truncate font-bold text-gray-900 leading-tight">
                                                            {m.merchantName}
                                                        </div>
                                                        <div className="flex items-center gap-x-1.5 text-[10px] text-gray-400 font-medium">
                                                            {m.currency && <span>{m.currency}</span>}
                                                            {m.currency && <span>•</span>}
                                                            <span className="capitalize">
                                                                {m.userRole === 'owner' ? (t('_.owner') || 'Owner') : (t('_.sub_user') || 'Sub-User')}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>
                                                {isCurrent && (
                                                    <CheckIcon className="h-4 w-4 text-[#0b6a8c] shrink-0 ltr:ml-2 rtl:mr-2" aria-hidden="true"/>
                                                )}
                                            </button>
                                        )}
                                    </Menu.Item>
                                );
                            })}
                        </div>
                    </Menu.Items>
                </Transition>
            )}
        </Menu>
    );
}

export default MerchantSwitcher;
